/**
 * CoachingConversation — the coaching UI shell.
 *
 * Implements show-not-tell/TS-004 (T4) and show-not-tell/US-004 (T5).
 * Renders the ordered coaching message list (coach prompts, the child's
 * tell, hints, draft attempts, feedback, and any reveal — visually
 * distinguished and in strict received order), an auto-scrolling
 * container, and the child's reply input.
 *
 * Reuses ChatInput verbatim for the input+send (frontend/src/components/ChatInput.tsx)
 * and models its bubble styling on MessageBubble's role-based left/right
 * treatment, but uses the slice-owned WritingMessage {role:'coach'|'child'}
 * shape (frontend/src/types/writingPractice.ts) rather than the comprehension
 * Message type, preserving slice isolation (BR-TS004.2 / BR-009).
 *
 * Accessibility (AC-TS004.5 / AC-004.5): the message list is a role="log"
 * aria-live="polite" region so assistive tech announces new coach/child
 * turns; the gentle error banner uses role="alert"; every icon-only control
 * carries an aria-label; ChatInput's Send button (Button size="icon") and
 * the Retry/Close controls are h-11 (44px) minimum touch targets per the
 * shared Button component (frontend/src/components/ui/button.tsx).
 *
 * Enforces DD-3/DD-005 (never a raw 5xx to a child): the `error` prop is
 * always pre-mapped gentle copy from useWritingSession's mapErrorToGentleCopy
 * — this component never receives or renders a raw status/stack.
 *
 * readOnly (show-not-tell/US-011): when a REOPENED historical session is
 * being viewed (WritingPracticePage.openSession), the transcript renders
 * without the reply input or error affordances — there is nothing to send
 * to a completed session. `onSend`/`onRetry`/`onClose` remain required props
 * so the active-session call sites are unaffected; they are simply unused
 * while readOnly is true.
 */

import { useEffect, useRef, useState } from 'react';
import { ChatInput } from '@/components/ChatInput';
import { Button } from '@/components/ui/button';
import type { WritingMessage } from '@/types/writingPractice';
import type { WritingGentleError } from '@/hooks/useWritingSession';
// BUG-20260717-003154 (RC-004, T-4): shared avatar assets, replicating
// MessageBubble's avatar markup (frontend/src/components/MessageBubble.tsx)
// WITHOUT importing the comprehension component itself, preserving slice
// isolation (BR-TS004.2/BR-009).
import tutorAvatar from '@/assets/tutor-avatar.svg';
import tutorIcon from '@/assets/tutor-icon.png';
import studentAvatar from '@/assets/student-avatar.png';

export interface CoachingConversationProps {
  messages: WritingMessage[];
  isSending: boolean;
  error?: WritingGentleError;
  onSend: (text: string) => Promise<void>;
  onRetry: () => void;
  onClose: () => void;
  /** show-not-tell/US-011: render the transcript only, no reply input. */
  readOnly?: boolean;
  /**
   * BUG-20260717-003154 (RC-004, T-5): custom profile image URL for the
   * student avatar, sourced from usePebbles by WritingPracticePage — mirrors
   * MessageBubble's profileImageUrl prop (BUG-20260209-105903) rather than a
   * per-message field.
   */
  profileImageUrl?: string | null;
}

const KIND_LABEL: Partial<Record<NonNullable<WritingMessage['kind']>, string>> = {
  hint: 'Hint',
  draft: 'Your draft',
  feedback: 'Feedback',
  reveal: 'Here it is',
};

/**
 * WritingMessageBubble — slice-owned bubble with role-based tutor/student
 * avatars.
 *
 * BUG-20260717-003154 (RC-004, T-4/T-5): replicates MessageBubble's avatar
 * markup and onError fallback pattern (frontend/src/components/MessageBubble.tsx
 * lines ~39-44, 90-98, 174-183) so the writing-practice chat shows a tutor
 * (otter) avatar left of every coach message and a student avatar (custom
 * profile photo if set, else the default) right of every child message —
 * WITHOUT importing the comprehension MessageBubble component, preserving
 * slice isolation (BR-TS004.2/BR-009). All existing bubble/label/reveal
 * markup is preserved verbatim; only the avatar <img> elements and the
 * items-end/gap-2 wrapper classes are new.
 */
function WritingMessageBubble({
  message: m,
  profileImageUrl,
}: {
  message: WritingMessage;
  profileImageUrl?: string | null;
}) {
  const isChild = m.role === 'child';
  const isReveal = m.kind === 'reveal';
  const label = m.kind ? KIND_LABEL[m.kind] : undefined;

  const [tutorIconError, setTutorIconError] = useState(false);
  const [studentIconError, setStudentIconError] = useState(false);
  const tutorAvatarSrc = tutorIconError ? tutorAvatar : tutorIcon;

  return (
    <div
      className={`flex mb-3 items-end gap-2 ${isChild ? 'justify-end' : 'justify-start'}`}
    >
      {/* Tutor avatar on left (coach messages) */}
      {!isChild && (
        <img
          src={tutorAvatarSrc}
          alt=""
          aria-hidden="true"
          className="w-11 h-11 rounded-full flex-shrink-0 mb-1 object-cover border-2 border-white shadow-sm"
          onError={() => setTutorIconError(true)}
        />
      )}

      <div
        className={`max-w-[85%] md:max-w-[70%] px-4 py-2.5 rounded-2xl text-sm md:text-base leading-relaxed ${
          isChild
            ? 'bg-gradient-to-br from-otter-orange-400 to-otter-orange-500 text-white rounded-br-md shadow-sm'
            : isReveal
              ? 'bg-otter-orange-50 border-2 border-otter-orange-300 text-otter-earth-800 rounded-bl-md shadow-sm'
              : 'bg-white border border-otter-sand text-otter-earth-800 rounded-bl-md shadow-sm'
        }`}
      >
        {label && (
          <span className="block text-xs font-medium mb-1 uppercase tracking-wide opacity-70">
            {label}
          </span>
        )}
        <p className="whitespace-pre-wrap">{m.text}</p>
      </div>

      {/* Student avatar on right (child messages), custom photo with fallback */}
      {isChild && (
        <img
          src={profileImageUrl && !studentIconError ? profileImageUrl : studentAvatar}
          alt=""
          aria-hidden="true"
          className="w-8 h-8 rounded-full flex-shrink-0 mb-1 object-cover border-2 border-white shadow-sm"
          onError={() => setStudentIconError(true)}
        />
      )}
    </div>
  );
}

/**
 * CoachingConversation — purely presentational. All state/actions come via
 * props from WritingPracticePage (T5/T6); no direct API calls, no
 * comprehension imports.
 */
export function CoachingConversation({
  messages,
  isSending,
  error,
  onSend,
  onRetry,
  onClose,
  readOnly = false,
  profileImageUrl,
}: CoachingConversationProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        ref={containerRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        className="flex-1 overflow-y-auto px-3 md:px-4 py-4 space-y-1"
      >
        {messages.length === 0 && !isSending && (
          <div className="flex items-center justify-center h-full">
            <p className="text-center text-gray-500 text-sm">
              Your coach is ready. Say hello to get started!
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <WritingMessageBubble key={i} message={m} profileImageUrl={profileImageUrl} />
        ))}
        {isSending && (
          <div className="flex justify-start mb-3" aria-hidden="true">
            <div className="px-4 py-2.5 rounded-2xl bg-white border border-otter-sand">
              <span className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-otter-earth-300 animate-bounce [animation-delay:-0.3s]" />
                <span className="h-2 w-2 rounded-full bg-otter-earth-300 animate-bounce [animation-delay:-0.15s]" />
                <span className="h-2 w-2 rounded-full bg-otter-earth-300 animate-bounce" />
              </span>
            </div>
          </div>
        )}
      </div>

      {!readOnly && error && (
        <div
          role="alert"
          className="mx-3 md:mx-4 mb-2 p-3 bg-otter-orange-50 border border-otter-orange-200 rounded-lg text-sm text-otter-earth-800 flex items-center justify-between gap-2"
        >
          <span>{error.message}</span>
          <div className="flex gap-2 flex-shrink-0">
            {error.kind === 'retry' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 md:h-9"
                aria-label="Try again"
                onClick={onRetry}
              >
                Try again
              </Button>
            )}
            {error.kind === 'close' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 md:h-9"
                aria-label="Back to dashboard"
                onClick={onClose}
              >
                Back to dashboard
              </Button>
            )}
          </div>
        </div>
      )}

      {!readOnly && <ChatInput disabled={isSending} onSubmit={onSend} />}
    </div>
  );
}
